/**
 * 微信 JSSDK 封装
 * 提供配置、支付等常用功能
 */
import wx from 'weixin-js-sdk';

import apmLogger from '@/utils/apmplusMonitor';
import { reportCustomError } from '@/utils/feishu';
import menuShare = wx.menuShare;
import menuProtected = wx.menuProtected;

/**
 * 微信 JSSDK 配置参数
 * 注意：timestamp 必须是 number 类型（根据官方文档）
 */
export interface WeixinConfigParams {
  appId: string;
  /** 生成签名的时间戳（必须是 number 类型） */
  timestamp: number;
  /** 生成签名的随机串 */
  nonceStr: string;
  /** 签名 */
  signature: string;
  debugger?: boolean;
}

/**
 * 微信支付参数
 * 注意：支付接口的 timestamp 是 string 类型，与 config 的 timestamp (number) 不同
 *
 * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E5%BE%AE%E4%BF%A1%E6%94%AF%E4%BB%98
 */
export type WeixinPayParams = Pick<wx.IchooseWXPay, 'timestamp' | 'nonceStr' | 'package' | 'signType' | 'paySign'>;

/**
 * 微信 JSSDK 工具类
 */
class WeixinSDK {
  /** 微信 sdk 初始化 AppID */
  private appId: string = import.meta.env.VITE_WECHAT_MP_APPID || '';
  /** 微信 JSSDK 是否已就绪 */
  private isConfigured = false;
  /** 微信 JSSDK 配置 Promise */
  private configPromise: Promise<void> | null = null;

  /**
   * 配置微信 JSSDK
   * @param params 配置参数
   * @returns Promise
   */
  config(params: WeixinConfigParams) {
    // 如果已经配置过，直接返回之前的 Promise
    if (this.configPromise) {
      return this.configPromise;
    }
    this.configPromise = new Promise((resolve, reject) => {
      wx.config({
        appId: params.appId,
        timestamp: params.timestamp,
        nonceStr: params.nonceStr,
        signature: params.signature,
        debug: params.debugger || false,
        jsApiList: [
          'updateAppMessageShareData',
          'updateTimelineShareData',
          'chooseWXPay',
          'getNetworkType',
          'closeWindow',
          'hideMenuItems',
        ],
        openTagList: ['wx-open-launch-app'],
      });

      wx.ready(() => {
        this.isConfigured = true;
        apmLogger.info('[WeixinSDK] 微信 JSSDK 配置成功 (wx.ready)');
        resolve();
      });

      wx.error((err: any) => {
        apmLogger.error('[WeixinSDK] 微信 JSSDK 配置失败 (wx.error)', { err: JSON.stringify(err) });
        this.isConfigured = false;
        this.configPromise = null;
        reject(err);
      });
    });

    return this.configPromise;
  }

  /**
   * 调起微信支付
   * @param params 支付参数
   * @returns Promise
   */
  async pay(params: WeixinPayParams): Promise<void> {
    return new Promise((resolve, reject) => {
      wx.chooseWXPay({
        timestamp: params.timestamp,
        nonceStr: params.nonceStr,
        package: params.package,
        signType: params.signType,
        paySign: params.paySign,
        cancel: (...args) => {
          apmLogger.info('[WeixinSDK] 微信支付取消', { args: JSON.stringify(args) });
          reject({ errMsg: 'chooseWXPay:cancel' });
        },
        success: (res) => {
          apmLogger.info('[WeixinSDK] 微信支付成功', { res: JSON.stringify(res) });
          resolve(res);
        },
        fail: (err) => {
          apmLogger.error('[WeixinSDK] 微信支付失败', { err: JSON.stringify(err) });
          reportCustomError('[WeixinSDK] 微信支付失败', { stack: JSON.stringify(err) });
          reject(err);
        },
      });
    });
  }

  /**
   * 获取网络状态
   * @returns Promise<string> 网络类型（wifi/2g/3g/4g/5g/unknown/none）
   */
  async getNetworkType(): Promise<string> {
    return new Promise((resolve, reject) => {
      wx.getNetworkType({
        success: (res) => resolve(res.networkType),
        fail: reject,
      });
    });
  }

  /** 隐藏所有分享菜单 */
  async hideShareMenu({ showAppMessage = true, showBrowser = false }): Promise<void> {
    const menuList: (menuProtected | menuShare)[] = [
      'menuItem:share:timeline',
      'menuItem:share:qq',
      'menuItem:share:weiboApp',
      'menuItem:share:facebook',
      'menuItem:favorite',
      'menuItem:share:QZone',
      'menuItem:delete',
      'menuItem:copyUrl',
      'menuItem:readMode',
      'menuItem:share:email',
      'menuItem:editTag',
      'menuItem:share:brand',
    ];
    if (!showAppMessage) {
      menuList.push('menuItem:share:appMessage');
    }

    if (!showBrowser) {
      menuList.push('menuItem:originPage');
      menuList.push('menuItem:openWithQQBrowser');
      menuList.push('menuItem:openWithSafari');
    }
    wx.hideMenuItems({ menuList });
  }

  /**
   * 关闭当前网页窗口
   */
  closeWindow(): void {
    wx.closeWindow();
  }

  /**
   * 分享到朋友圈
   * @param params 分享参数
   * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E5%88%86%E4%BA%AB%E6%8E%A5%E5%8F%A3
   */
  shareToTimeline(params: wx.IupdateTimelineShareData): void {
    if (!this.isConfigured) {
      apmLogger.warn('[WeixinSDK] shareToTimeline: JSSDK 未就绪，等待 ready 后执行');
      wx.ready(() => {
        wx.updateTimelineShareData(params);
      });
      return;
    }
    wx.updateTimelineShareData(params);
  }

  /**
   * 分享给朋友
   */
  shareToFriend(params: { title: string; desc?: string; link: string; imgUrl?: string }): void {
    wx.updateAppMessageShareData({
      title: params.title,
      desc: params.desc || '',
      link: params.link,
      imgUrl: params.imgUrl || 'https://cdn.8848top.com/project/prod/be-link-pandora/logo.png',
      success: () => {},
    });
  }

  /**
   * 检查是否已配置
   */
  isReady(): boolean {
    return this.isConfigured;
  }

  /**
   * 重置配置状态（用于切换账号等场景）
   */
  reset(): void {
    this.isConfigured = false;
    this.configPromise = null;
  }
}

/**
 * APP 内嵌 WebView 收到 RN 的支付失败文案时，将微信 errCode 数字串（如 -2）等转为可读提示
 */
export function formatAppPayErrorMessage(raw?: string): string {
  if (raw == null || raw === '') return '支付失败，请重试';
  const t = String(raw).trim();
  if (t === '-2') return '您已取消支付';
  if (t === '用户取消' || t === '用户取消了支付') return '您已取消支付';
  return t;
}

// 导出单例
export const weixinSDK = new WeixinSDK();

// 默认导出
export default weixinSDK;
